import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useGesture } from '@use-gesture/react';

// CSR Images
import csrImg1 from '../../images/csr/csr-01.jpg';
import csrImg2 from '../../images/csr/csr-02.jpg';
import csrImg3 from '../../images/csr/csr-03.jpg';
import csrImg4 from '../../images/csr/csr-04.jpg';
import csrImg5 from '../../images/csr/csr-05.jpg';
import csrImg6 from '../../images/csr/csr-06.jpg';
import csrImg7 from '../../images/csr/csr-07.jpg';
import csrImg8 from '../../images/csr/csr-08.jpg';
import csrImg9 from '../../images/csr/csr-09.jpg';
import csrImg10 from '../../images/csr/csr-10.jpg';
import csrImg11 from '../../images/csr/csr-11.jpg';
import csrImg12 from '../../images/csr/csr-12.jpg';
import csrImg13 from '../../images/csr/csr-13.jpg';
import csrImg14 from '../../images/csr/csr-14.jpg';
import csrImg15 from '../../images/csr/csr-15.jpg';
import csrImg16 from '../../images/csr/csr-16.jpg';
import csrImg17 from '../../images/csr/csr-17.jpg';
import csrImg18 from '../../images/csr/csr-18.jpg';

type ImageItem = string | { src: string; alt?: string };

type DomeGalleryProps = {
    images?: ImageItem[];
    fit?: number;
    fitBasis?: 'auto' | 'min' | 'max' | 'width' | 'height';
    minRadius?: number;
    maxRadius?: number;
    padFactor?: number;
    overlayBlurColor?: string;
    maxVerticalRotationDeg?: number;
    dragSensitivity?: number;
    enlargeTransitionMs?: number;
    segments?: number;
    dragDampening?: number;
    openedImageWidth?: string;
    openedImageHeight?: string;
    imageBorderRadius?: string;
    openedImageBorderRadius?: string;
    grayscale?: boolean;
};

type ItemDef = {
    src: string;
    alt: string;
    x: number;
    y: number;
    sizeX: number;
    sizeY: number;
};

const DEFAULT_IMAGES: ImageItem[] = [
    { src: csrImg1, alt: 'CSR Initiative' },
    { src: csrImg2, alt: 'Community Outreach' },
    { src: csrImg3, alt: 'Social Responsibility' },
    { src: csrImg4, alt: 'Giving Back' },
    { src: csrImg5, alt: 'Community Support' },
    { src: csrImg6, alt: 'CSR Activity' },
    { src: csrImg7, alt: 'Corporate Responsibility' },
    { src: csrImg8, alt: 'Making a Difference' },
    { src: csrImg9, alt: 'Community Engagement' },
    { src: csrImg10, alt: 'Social Impact' },
    { src: csrImg11, alt: 'CSR Program' },
    { src: csrImg12, alt: 'Community Development' },
    { src: csrImg13, alt: 'Social Initiative' },
    { src: csrImg14, alt: 'CSR Event' },
    { src: csrImg15, alt: 'Community Service' },
    { src: csrImg16, alt: 'CSR Volunteering' },
    { src: csrImg17, alt: 'Community Partnership' },
    { src: csrImg18, alt: 'Social Good' }
];

const DEFAULTS = {
    maxVerticalRotationDeg: 5,
    dragSensitivity: 20,
    enlargeTransitionMs: 300,
    segments: 35
};

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const normalizeAngle = (d: number) => ((d % 360) + 360) % 360;
const wrapAngleSigned = (deg: number) => {
    const a = (((deg + 180) % 360) + 360) % 360;
    return a - 180;
};
const getDataNumber = (el: HTMLElement, name: string, fallback: number) => {
    const attr = el.dataset[name] ?? el.getAttribute(`data-${name}`);
    const n = attr == null ? NaN : parseFloat(attr);
    return Number.isFinite(n) ? n : fallback;
};

function buildItems(pool: ImageItem[], seg: number): ItemDef[] {
    const xCols = Array.from({ length: seg }, (_, i) => -37 + i * 2);
    const evenYs = [-4, -2, 0, 2, 4];
    const oddYs = [-3, -1, 1, 3, 5];

    const coords = xCols.flatMap((x, c) => {
        const ys = c % 2 === 0 ? evenYs : oddYs;
        return ys.map(y => ({ x, y, sizeX: 2, sizeY: 2 }));
    });

    const totalSlots = coords.length;
    if (pool.length === 0) {
        return coords.map(c => ({ ...c, src: '', alt: '' }));
    }
    if (pool.length > totalSlots) {
        console.warn(
            `[DomeGallery] Provided image count (${pool.length}) exceeds available tiles (${totalSlots}). Some images will not be shown.`
        );
    }

    const normalizedImages = pool.map(image => {
        if (typeof image === 'string') {
            return { src: image, alt: '' };
        }
        return { src: image.src || '', alt: image.alt || '' };
    });

    const usedImages = Array.from({ length: totalSlots }, (_, i) => normalizedImages[i % normalizedImages.length]);

    for (let i = 1; i < usedImages.length; i++) {
        if (usedImages[i].src === usedImages[i - 1].src) {
            for (let j = i + 1; j < usedImages.length; j++) {
                if (usedImages[j].src !== usedImages[i].src) {
                    const tmp = usedImages[i];
                    usedImages[i] = usedImages[j];
                    usedImages[j] = tmp;
                    break;
                }
            }
        }
    }

    return coords.map((c, i) => ({
        ...c,
        src: usedImages[i].src,
        alt: usedImages[i].alt
    }));
}

function computeItemBaseRotation(offsetX: number, offsetY: number, sizeX: number, sizeY: number, segments: number) {
    const unit = 360 / segments / 2;
    const rotateY = unit * (offsetX + (sizeX - 1) / 2);
    const rotateX = unit * (offsetY - (sizeY - 1) / 2);
    return { rotateX, rotateY };
}

export default function DomeGallery({
    images = DEFAULT_IMAGES,
    fit = 0.5,
    fitBasis = 'auto',
    minRadius = 600,
    maxRadius = Infinity,
    padFactor = 0.25,
    overlayBlurColor = '#060010',
    maxVerticalRotationDeg = DEFAULTS.maxVerticalRotationDeg,
    dragSensitivity = DEFAULTS.dragSensitivity,
    enlargeTransitionMs = DEFAULTS.enlargeTransitionMs,
    segments = DEFAULTS.segments,
    dragDampening = 2,
    openedImageWidth = '400px',
    openedImageHeight = '400px',
    imageBorderRadius = '30px',
    openedImageBorderRadius = '30px',
    grayscale = true
}: DomeGalleryProps) {
    const rootRef = useRef<HTMLDivElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const sphereRef = useRef<HTMLDivElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const viewerRef = useRef<HTMLDivElement>(null);
    const scrimRef = useRef<HTMLDivElement>(null);
    const focusedElRef = useRef<HTMLElement | null>(null);
    const originalTilePositionRef = useRef<{
        left: number;
        top: number;
        width: number;
        height: number;
    } | null>(null);
    const closeHandlerRef = useRef<(() => void) | null>(null);

    const rotationRef = useRef({ x: 0, y: 0 });
    const startRotRef = useRef({ x: 0, y: 0 });
    const startPosRef = useRef<{ x: number; y: number } | null>(null);
    const draggingRef = useRef(false);
    const cancelTapRef = useRef(false);
    const movedRef = useRef(false);
    const inertiaRAF = useRef<number | null>(null);
    const pointerTypeRef = useRef<'mouse' | 'pen' | 'touch'>('mouse');
    const tapTargetRef = useRef<HTMLElement | null>(null);
    const openingRef = useRef(false);
    const openStartedAtRef = useRef(0);
    const lastDragEndAt = useRef(0);

    const scrollLockedRef = useRef(false);
    const lockScroll = useCallback(() => {
        if (scrollLockedRef.current) return;
        scrollLockedRef.current = true;
        document.body.classList.add('dg-scroll-lock');
    }, []);
    const unlockScroll = useCallback(() => {
        if (!scrollLockedRef.current) return;
        if (rootRef.current?.getAttribute('data-enlarging') === 'true') return;
        scrollLockedRef.current = false;
        document.body.classList.remove('dg-scroll-lock');
    }, []);

    const items = useMemo(() => buildItems(images, segments), [images, segments]);

    const applyTransform = (xDeg: number, yDeg: number) => {
        const el = sphereRef.current;
        if (el) {
            el.style.transform = `translateZ(calc(var(--radius) * -1)) rotateX(${xDeg}deg) rotateY(${yDeg}deg)`;
        }
    };

    const lockedRadiusRef = useRef<number | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        if (!root) return;
        const ro = new ResizeObserver(entries => {
            // Use requestAnimationFrame to prevent ResizeObserver loop errors
            requestAnimationFrame(() => {
                if (!entries[0]) return;
                const cr = entries[0].contentRect;
                const w = Math.max(1, cr.width),
                    h = Math.max(1, cr.height);
                const minDim = Math.min(w, h),
                    maxDim = Math.max(w, h),
                    aspect = w / h;
                let basis: number;
                switch (fitBasis) {
                    case 'min':
                        basis = minDim;
                        break;
                    case 'max':
                        basis = maxDim;
                        break;
                    case 'width':
                        basis = w;
                        break;
                    case 'height':
                        basis = h;
                        break;
                    default:
                        basis = aspect >= 1.3 ? w : minDim;
                }
                let radius = basis * fit;
                const heightGuard = h * 1.35;
                radius = Math.min(radius, heightGuard);
                radius = clamp(radius, minRadius, maxRadius);
                lockedRadiusRef.current = Math.round(radius);

                const viewerPad = Math.max(8, Math.round(minDim * padFactor));
                root.style.setProperty('--radius', `${lockedRadiusRef.current}px`);
                root.style.setProperty('--viewer-pad', `${viewerPad}px`);
                root.style.setProperty('--overlay-blur-color', overlayBlurColor);
                root.style.setProperty('--tile-radius', imageBorderRadius);
                root.style.setProperty('--enlarge-radius', openedImageBorderRadius);
                root.style.setProperty('--image-filter', grayscale ? 'grayscale(1)' : 'none');
                applyTransform(rotationRef.current.x, rotationRef.current.y);


                // Handle resizing of fixed position overlay
                const enlargedOverlay = document.body.querySelector('.enlarge') as HTMLElement;
                if (enlargedOverlay) {
                    const isMobile = window.innerWidth < 768;
                    const isSmallMobile = window.innerWidth < 480;

                    const mobilePadding = isSmallMobile ? 40 : 60;
                    const desktopPadding = 120;
                    const padding = isMobile ? mobilePadding : desktopPadding;

                    const maxWidth = window.innerWidth - padding;
                    const maxHeight = window.innerHeight - padding;

                    let targetWidth = openedImageWidth ? parseInt(openedImageWidth) : 550;
                    let targetHeight = openedImageHeight ? parseInt(openedImageHeight) : 420;

                    if (isMobile) {
                        targetWidth = Math.min(targetWidth, maxWidth);
                        targetHeight = Math.min(targetHeight, maxHeight);

                        const aspectRatio = targetWidth / targetHeight;
                        if (targetWidth > maxWidth) {
                            targetWidth = maxWidth;
                            targetHeight = targetWidth / aspectRatio;
                        }
                        if (targetHeight > maxHeight) {
                            targetHeight = maxHeight;
                            targetWidth = targetHeight * aspectRatio;
                        }
                    } else {
                        targetWidth = Math.min(targetWidth, maxWidth);
                        targetHeight = Math.min(targetHeight, maxHeight);
                    }

                    // Recenter in viewport
                    const centerX = (window.innerWidth - targetWidth) / 2;
                    const centerY = (window.innerHeight - targetHeight) / 2;

                    enlargedOverlay.style.left = `${centerX}px`;
                    enlargedOverlay.style.top = `${centerY}px`;
                    enlargedOverlay.style.width = `${targetWidth}px`;
                    enlargedOverlay.style.height = `${targetHeight}px`;
                }
            });
        });
        ro.observe(root);
        return () => ro.disconnect();
    }, [
        fit,
        fitBasis,
        minRadius,
        maxRadius,
        padFactor,
        overlayBlurColor,
        grayscale,
        imageBorderRadius,
        openedImageBorderRadius,
        openedImageWidth,
        openedImageHeight
    ]);

    useEffect(() => {
        applyTransform(rotationRef.current.x, rotationRef.current.y);
    }, []);

    const stopInertia = useCallback(() => {
        if (inertiaRAF.current) {
            cancelAnimationFrame(inertiaRAF.current);
            inertiaRAF.current = null;
        }
    }, []);

    const startInertia = useCallback(
        (vx: number, vy: number) => {
            const MAX_V = 1.4;
            let vX = clamp(vx, -MAX_V, MAX_V) * 80;
            let vY = clamp(vy, -MAX_V, MAX_V) * 80;
            let frames = 0;
            const d = clamp(dragDampening ?? 0.6, 0, 1);
            const frictionMul = 0.94 + 0.055 * d;
            const stopThreshold = 0.015 - 0.01 * d;
            const maxFrames = Math.round(90 + 270 * d);
            const step = () => {
                vX *= frictionMul;
                vY *= frictionMul;
                if (Math.abs(vX) < stopThreshold && Math.abs(vY) < stopThreshold) {
                    inertiaRAF.current = null;
                    return;
                }
                if (++frames > maxFrames) {
                    inertiaRAF.current = null;
                    return;
                }
                const nextX = clamp(rotationRef.current.x - vY / 200, -maxVerticalRotationDeg, maxVerticalRotationDeg);
                const nextY = wrapAngleSigned(rotationRef.current.y + vX / 200);
                rotationRef.current = { x: nextX, y: nextY };
                applyTransform(nextX, nextY);
                inertiaRAF.current = requestAnimationFrame(step);
            };
            stopInertia();
            inertiaRAF.current = requestAnimationFrame(step);
        },
        [dragDampening, maxVerticalRotationDeg, stopInertia]
    );

    useGesture(
        {
            onDragStart: ({ event }) => {
                if (focusedElRef.current) return;
                stopInertia();

                const evt = event as PointerEvent;
                pointerTypeRef.current = (evt.pointerType as any) || 'mouse';
                if (pointerTypeRef.current === 'touch') evt.preventDefault();
                if (pointerTypeRef.current === 'touch') lockScroll();
                draggingRef.current = true;
                cancelTapRef.current = false;
                movedRef.current = false;
                startRotRef.current = { ...rotationRef.current };
                startPosRef.current = { x: evt.clientX, y: evt.clientY };
                const potential = (evt.target as Element).closest?.('.item__image') as HTMLElement | null;
                tapTargetRef.current = potential || null;
            },
            onDrag: ({ event, last, velocity: velArr = [0, 0], direction: dirArr = [0, 0], movement }) => {
                if (focusedElRef.current || !draggingRef.current || !startPosRef.current) return;

                const evt = event as PointerEvent;
                if (pointerTypeRef.current === 'touch') evt.preventDefault();

                const dxTotal = evt.clientX - startPosRef.current.x;
                const dyTotal = evt.clientY - startPosRef.current.y;

                if (!movedRef.current) {
                    const dist2 = dxTotal * dxTotal + dyTotal * dyTotal;
                    if (dist2 > 16) movedRef.current = true;
                }

                const nextX = clamp(
                    startRotRef.current.x - dyTotal / dragSensitivity,
                    -maxVerticalRotationDeg,
                    maxVerticalRotationDeg
                );
                const nextY = startRotRef.current.y + dxTotal / dragSensitivity;

                const cur = rotationRef.current;
                if (cur.x !== nextX || cur.y !== nextY) {
                    rotationRef.current = { x: nextX, y: nextY };
                    applyTransform(nextX, nextY);
                }

                if (last) {
                    draggingRef.current = false;
                    let isTap = false;

                    if (startPosRef.current) {
                        const dx = evt.clientX - startPosRef.current.x;
                        const dy = evt.clientY - startPosRef.current.y;
                        const dist2 = dx * dx + dy * dy;
                        const TAP_THRESH_PX = pointerTypeRef.current === 'touch' ? 10 : 6;
                        if (dist2 <= TAP_THRESH_PX * TAP_THRESH_PX) {
                            isTap = true;
                        }
                    }

                    let [vMagX, vMagY] = velArr;
                    const [dirX, dirY] = dirArr;
                    let vx = vMagX * dirX;
                    let vy = vMagY * dirY;

                    if (!isTap && Math.abs(vx) < 0.001 && Math.abs(vy) < 0.001 && Array.isArray(movement)) {
                        const [mx, my] = movement;
                        vx = (mx / dragSensitivity) * 0.02;
                        vy = (my / dragSensitivity) * 0.02;
                    }

                    if (!isTap && (Math.abs(vx) > 0.005 || Math.abs(vy) > 0.005)) {
                        startInertia(vx, vy);
                    }
                    startPosRef.current = null;
                    cancelTapRef.current = !isTap;

                    if (isTap && tapTargetRef.current && !focusedElRef.current) {
                        openItemFromElement(tapTargetRef.current);
                    }
                    tapTargetRef.current = null;

                    if (cancelTapRef.current) setTimeout(() => (cancelTapRef.current = false), 120);
                    if (pointerTypeRef.current === 'touch') unlockScroll();
                    if (movedRef.current) lastDragEndAt.current = performance.now();
                    movedRef.current = false;
                }
            }
        },
        { target: mainRef, eventOptions: { passive: false } }
    );

    useEffect(() => {
        const scrim = scrimRef.current;
        if (!scrim) return;

        const close = () => {
            if (performance.now() - openStartedAtRef.current < 250) return;
            const el = focusedElRef.current;
            if (!el) return;
            const parent = el.parentElement as HTMLElement;

            // Find overlay and backdrop from document.body
            const overlay = document.body.querySelector('.enlarge') as HTMLElement | null;
            const backdrop = document.body.querySelector('.enlarge-backdrop') as HTMLElement | null;

            if (!overlay) return;

            const refDiv = parent.querySelector('.item__image--reference') as HTMLElement | null;

            const originalPos = originalTilePositionRef.current;
            if (!originalPos) {
                overlay.remove();
                backdrop?.remove();
                if (refDiv) refDiv.remove();
                parent.style.setProperty('--rot-y-delta', `0deg`);
                parent.style.setProperty('--rot-x-delta', `0deg`);
                el.style.visibility = '';
                (el.style as any).zIndex = 0;
                focusedElRef.current = null;
                rootRef.current?.removeAttribute('data-enlarging');
                openingRef.current = false;
                document.body.classList.remove('dg-scroll-lock');
                return;
            }

            // Animate overlay back to original position
            overlay.style.transition = `all ${enlargeTransitionMs}ms ease-out`;
            overlay.style.left = `${originalPos.left}px`;
            overlay.style.top = `${originalPos.top}px`;
            overlay.style.width = `${originalPos.width}px`;
            overlay.style.height = `${originalPos.height}px`;
            overlay.style.opacity = '0';

            // Fade out backdrop
            if (backdrop) {
                backdrop.style.opacity = '0';
            }

            const cleanup = () => {
                overlay.remove();
                backdrop?.remove();
                originalTilePositionRef.current = null;

                if (refDiv) refDiv.remove();
                parent.style.transition = 'none';
                el.style.transition = 'none';

                parent.style.setProperty('--rot-y-delta', `0deg`);
                parent.style.setProperty('--rot-x-delta', `0deg`);

                requestAnimationFrame(() => {
                    el.style.visibility = '';
                    el.style.opacity = '0';
                    (el.style as any).zIndex = 0;
                    focusedElRef.current = null;
                    rootRef.current?.removeAttribute('data-enlarging');

                    requestAnimationFrame(() => {
                        parent.style.transition = '';
                        el.style.transition = 'opacity 300ms ease-out';

                        requestAnimationFrame(() => {
                            el.style.opacity = '1';
                            setTimeout(() => {
                                el.style.transition = '';
                                el.style.opacity = '';
                                openingRef.current = false;
                                document.body.classList.remove('dg-scroll-lock');
                            }, 300);
                        });
                    });
                });
            };

            overlay.addEventListener('transitionend', cleanup, {
                once: true
            });
        };

        // Store close function in ref so it can be called from backdrop
        closeHandlerRef.current = close;

        scrim.addEventListener('click', close);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeHandlerRef.current?.();
            }
        };
        window.addEventListener('keydown', onKey);

        return () => {
            scrim.removeEventListener('click', close);
            window.removeEventListener('keydown', onKey);
            closeHandlerRef.current = null;
        };
    }, [enlargeTransitionMs, openedImageBorderRadius, grayscale]);

    const openItemFromElement = (el: HTMLElement) => {
        if (openingRef.current) return;
        openingRef.current = true;
        openStartedAtRef.current = performance.now();
        lockScroll();
        const parent = el.parentElement as HTMLElement;
        focusedElRef.current = el;
        el.setAttribute('data-focused', 'true');
        const offsetX = getDataNumber(parent, 'offsetX', 0);
        const offsetY = getDataNumber(parent, 'offsetY', 0);
        const sizeX = getDataNumber(parent, 'sizeX', 2);
        const sizeY = getDataNumber(parent, 'sizeY', 2);
        const parentRot = computeItemBaseRotation(offsetX, offsetY, sizeX, sizeY, segments);
        const parentY = normalizeAngle(parentRot.rotateY);
        const globalY = normalizeAngle(rotationRef.current.y);
        let rotY = -(parentY + globalY) % 360;
        if (rotY < -180) rotY += 360;
        const rotX = -parentRot.rotateX - rotationRef.current.x;
        parent.style.setProperty('--rot-y-delta', `${rotY}deg`);
        parent.style.setProperty('--rot-x-delta', `${rotX}deg`);
        const refDiv = document.createElement('div');
        refDiv.className = 'item__image item__image--reference opacity-0';
        refDiv.style.transform = `rotateX(${-parentRot.rotateX}deg) rotateY(${-parentRot.rotateY}deg)`;
        parent.appendChild(refDiv);

        void refDiv.offsetHeight;

        const tileR = refDiv.getBoundingClientRect();
        const mainR = mainRef.current?.getBoundingClientRect();
        const frameR = frameRef.current?.getBoundingClientRect();

        if (!mainR || !frameR || tileR.width <= 0 || tileR.height <= 0) {
            openingRef.current = false;
            focusedElRef.current = null;
            parent.removeChild(refDiv);
            unlockScroll();
            return;
        }

        originalTilePositionRef.current = {
            left: tileR.left,
            top: tileR.top,
            width: tileR.width,
            height: tileR.height
        };
        el.style.visibility = 'hidden';
        (el.style as any).zIndex = 0;

        // Calculate target size with mobile responsiveness
        const isMobile = window.innerWidth < 768;
        const isSmallMobile = window.innerWidth < 480;

        // Use larger percentage of viewport on mobile, with safe padding
        const mobilePadding = isSmallMobile ? 40 : 60;
        const desktopPadding = 120;
        const padding = isMobile ? mobilePadding : desktopPadding;

        const maxWidth = window.innerWidth - padding;
        const maxHeight = window.innerHeight - padding;

        let targetWidth = openedImageWidth ? parseInt(openedImageWidth) : 550;
        let targetHeight = openedImageHeight ? parseInt(openedImageHeight) : 420;

        // On mobile, use viewport-based sizing
        if (isMobile) {
            targetWidth = Math.min(targetWidth, maxWidth);
            targetHeight = Math.min(targetHeight, maxHeight);

            // Maintain aspect ratio on small screens
            const aspectRatio = targetWidth / targetHeight;
            if (targetWidth > maxWidth) {
                targetWidth = maxWidth;
                targetHeight = targetWidth / aspectRatio;
            }
            if (targetHeight > maxHeight) {
                targetHeight = maxHeight;
                targetWidth = targetHeight * aspectRatio;
            }
        } else {
            targetWidth = Math.min(targetWidth, maxWidth);
            targetHeight = Math.min(targetHeight, maxHeight);
        }

        // Calculate center position in viewport
        const centerX = (window.innerWidth - targetWidth) / 2;
        const centerY = (window.innerHeight - targetHeight) / 2;

        const overlay = document.createElement('div');
        overlay.className = 'enlarge';
        overlay.style.cssText = `
            position: fixed;
            left: ${tileR.left}px;
            top: ${tileR.top}px;
            width: ${tileR.width}px;
            height: ${tileR.height}px;
            opacity: 0;
            z-index: 10000;
            will-change: transform, opacity, width, height, left, top;
            transition: all ${enlargeTransitionMs}ms ease;
            border-radius: ${openedImageBorderRadius};
            overflow: hidden;
            box-shadow: 0 10px 30px rgba(0,0,0,.35);
        `;

        const rawSrc = parent.dataset.src || (el.querySelector('img') as HTMLImageElement)?.src || '';
        const rawAlt = parent.dataset.alt || (el.querySelector('img') as HTMLImageElement)?.alt || '';
        const img = document.createElement('img');
        img.src = rawSrc;
        img.alt = rawAlt;
        img.style.cssText = `width:100%; height:100%; object-fit:cover; filter:${grayscale ? 'grayscale(1)' : 'none'};`;
        overlay.appendChild(img);

        // Create backdrop for clicking to close
        const backdrop = document.createElement('div');
        backdrop.className = 'enlarge-backdrop';
        backdrop.style.cssText = `
            position: fixed;
            inset: 0;
            z-index: 9998;
            background: rgba(0, 0, 0, ${isMobile ? '0.85' : '0.7'});
            backdrop-filter: blur(${isMobile ? '4px' : '8px'});
            opacity: 0;
            transition: opacity ${enlargeTransitionMs}ms ease;
            cursor: ${isMobile ? 'default' : 'pointer'};
        `;

        // Add click handler to backdrop
        backdrop.addEventListener('click', () => {
            // Call close handler directly
            closeHandlerRef.current?.();
        });

        document.body.appendChild(backdrop);
        document.body.appendChild(overlay);

        // Force reflow
        void overlay.offsetWidth;

        // Animate to center
        requestAnimationFrame(() => {
            backdrop.style.opacity = '1';
            overlay.style.opacity = '1';
            overlay.style.left = `${centerX}px`;
            overlay.style.top = `${centerY}px`;
            overlay.style.width = `${targetWidth}px`;
            overlay.style.height = `${targetHeight}px`;
            rootRef.current?.setAttribute('data-enlarging', 'true');
        });
    };

    useEffect(() => {
        return () => {
            document.body.classList.remove('dg-scroll-lock');
        };
    }, []);

    const cssStyles = `
    .sphere-root {
      --radius: 520px;
      --viewer-pad: 72px;
      --circ: calc(var(--radius) * 3.14);
      --rot-y: calc((360deg / var(--segments-x)) / 2);
      --rot-x: calc((360deg / var(--segments-y)) / 2);
      --item-width: calc(var(--circ) / var(--segments-x));
      --item-height: calc(var(--circ) / var(--segments-y));
    }
    
    .sphere-root * { box-sizing: border-box; }
    .sphere, .sphere-item, .item__image { transform-style: preserve-3d; }
    
    .stage {
      width: 100%;
      height: 100%;
      display: grid;
      place-items: center;
      position: absolute;
      inset: 0;
      margin: auto;
      perspective: calc(var(--radius) * 2);
      perspective-origin: 50% 50%;
    }
    
    .sphere {
      transform: translateZ(calc(var(--radius) * -1));
      will-change: transform;
      position: absolute;
    }
    
    .sphere-item {
      width: calc(var(--item-width) * var(--item-size-x));
      height: calc(var(--item-height) * var(--item-size-y));
      position: absolute;
      top: -999px;
      bottom: -999px;
      left: -999px;
      right: -999px;
      margin: auto;
      transform-origin: 50% 50%;
      backface-visibility: hidden;
      transition: transform 300ms;
      transform: rotateY(calc(var(--rot-y) * (var(--offset-x) + ((var(--item-size-x) - 1) / 2)) + var(--rot-y-delta, 0deg))) 
                 rotateX(calc(var(--rot-x) * (var(--offset-y) - ((var(--item-size-y) - 1) / 2)) + var(--rot-x-delta, 0deg))) 
                 translateZ(var(--radius));
    }
    
    .sphere-root[data-enlarging="true"] .scrim {
      opacity: 1 !important;
      pointer-events: all !important;
    }
    
    @media (max-aspect-ratio: 1/1) {
      .viewer-frame {
        height: auto !important;
        width: 100% !important;
      }
    }
    
    body.dg-scroll-lock {
      overflow: hidden !important;
      touch-action: none !important;
      overscroll-behavior: contain !important;
    }
    
    .sphere-root {
      width: 100%;
      max-width: 100%;
      overflow: visible;
    }
    .item__image {
      position: absolute;
      inset: 10px;
      border-radius: var(--tile-radius, 12px);
      overflow: hidden;
      cursor: pointer;
      backface-visibility: hidden;
      -webkit-backface-visibility: hidden;
      transition: transform 300ms;
      pointer-events: auto;
      -webkit-transform: translateZ(0);
      transform: translateZ(0);
    }
    .item__image--reference {
      position: absolute;
      inset: 10px;
      pointer-events: none;
    }
    .item__image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      pointer-events: none;
    }
    .sphere-root > main {
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      display: grid;
      place-items: center;
      overflow: hidden;
      user-select: none;
      background: transparent;
    }
    .sphere-overlay {
      position: absolute;
      top: 0; right: 0; bottom: 0; left: 0;
      margin: auto;
      pointer-events: none;
      z-index: 3;
    }
    .sphere-overlay-gradient-top {
      position: absolute;
      left: 0; right: 0; top: 0;
      height: 120px;
      pointer-events: none;
      z-index: 5;
      transform: rotate(180deg);
    }
    .sphere-overlay-gradient-bottom {
      position: absolute;
      left: 0; right: 0; bottom: 0;
      height: 120px;
      pointer-events: none;
      z-index: 5;
    }
  `;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: cssStyles }} />
            <div
                ref={rootRef}
                className="sphere-root relative w-full h-full"
                style={
                    {
                        ['--segments-x' as any]: segments,
                        ['--segments-y' as any]: segments,
                        ['--overlay-blur-color' as any]: overlayBlurColor,
                        ['--tile-radius' as any]: imageBorderRadius,
                        ['--enlarge-radius' as any]: openedImageBorderRadius,
                        ['--image-filter' as any]: grayscale ? 'grayscale(1)' : 'none'
                    } as React.CSSProperties
                }
            >
                <main
                    ref={mainRef}
                    className="absolute inset-0 grid place-items-center overflow-hidden select-none bg-transparent"
                    style={{
                        touchAction: 'none',
                        WebkitUserSelect: 'none'
                    }}
                >
                    <div className="stage">
                        <div ref={sphereRef} className="sphere">
                            {items.map((it, i) => (
                                <div
                                    key={`${it.x},${it.y},${i}`}
                                    className="sphere-item absolute m-auto"
                                    data-src={it.src}
                                    data-alt={it.alt}
                                    data-offset-x={it.x}
                                    data-offset-y={it.y}
                                    data-size-x={it.sizeX}
                                    data-size-y={it.sizeY}
                                    style={
                                        {
                                            ['--offset-x' as any]: it.x,
                                            ['--offset-y' as any]: it.y,
                                            ['--item-size-x' as any]: it.sizeX,
                                            ['--item-size-y' as any]: it.sizeY,
                                            top: '-999px',
                                            bottom: '-999px',
                                            left: '-999px',
                                            right: '-999px'
                                        } as React.CSSProperties
                                    }
                                >
                                    <div
                                        className="item__image absolute block overflow-hidden cursor-pointer bg-gray-200 transition-transform duration-300"
                                        role="button"
                                        tabIndex={0}
                                        aria-label={it.alt || 'Open image'}
                                        onClick={e => {
                                            if (draggingRef.current) return;
                                            if (movedRef.current) return;
                                            if (performance.now() - lastDragEndAt.current < 80) return;
                                            if (openingRef.current) return;
                                            openItemFromElement(e.currentTarget as HTMLElement);
                                        }}
                                        onPointerUp={e => {
                                            if ((e.nativeEvent as PointerEvent).pointerType !== 'touch') return;
                                            if (draggingRef.current) return;
                                            if (movedRef.current) return;
                                            if (performance.now() - lastDragEndAt.current < 80) return;
                                            if (openingRef.current) return;
                                            openItemFromElement(e.currentTarget as HTMLElement);
                                        }}
                                        style={{
                                            inset: '10px',
                                            borderRadius: `var(--tile-radius, ${imageBorderRadius})`,
                                            backfaceVisibility: 'hidden'
                                        }}
                                    >
                                        <img
                                            src={it.src}
                                            draggable={false}
                                            alt={it.alt}
                                            className="w-full h-full object-cover pointer-events-none"
                                            style={{
                                                backfaceVisibility: 'hidden',
                                                filter: `var(--image-filter, ${grayscale ? 'grayscale(1)' : 'none'})`
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div
                        className="sphere-overlay"
                        style={{
                            backgroundImage: `radial-gradient(rgba(235, 235, 235, 0) 65%, var(--overlay-blur-color, ${overlayBlurColor}) 100%)`
                        }}
                    />

                    <div
                        className="sphere-overlay"
                        style={{
                            WebkitMaskImage: `radial-gradient(rgba(235, 235, 235, 0) 70%, var(--overlay-blur-color, ${overlayBlurColor}) 90%)`,
                            maskImage: `radial-gradient(rgba(235, 235, 235, 0) 70%, var(--overlay-blur-color, ${overlayBlurColor}) 90%)`,
                            backdropFilter: 'blur(3px)'
                        }}
                    />

                    <div
                        className="sphere-overlay-gradient-top"
                        style={{
                            background: `linear-gradient(to bottom, transparent, var(--overlay-blur-color, ${overlayBlurColor}))`
                        }}
                    />
                    <div
                        className="sphere-overlay-gradient-bottom"
                        style={{
                            background: `linear-gradient(to bottom, transparent, var(--overlay-blur-color, ${overlayBlurColor}))`
                        }}
                    />

                    <div
                        ref={viewerRef}
                        className="absolute inset-0 z-[100] pointer-events-none flex items-center justify-center"
                        style={{ padding: 'var(--viewer-pad)' }}
                    >
                        <div
                            ref={scrimRef}
                            className="scrim absolute inset-0 z-[90] pointer-events-none opacity-0 transition-opacity duration-500"
                            style={{
                                background: 'rgba(0, 0, 0, 0.7)',
                                backdropFilter: 'blur(8px)'
                            }}
                        />
                        <div
                            ref={frameRef}
                            className="viewer-frame h-full aspect-square flex relative z-[110]"
                            style={{
                                borderRadius: `var(--enlarge-radius, ${openedImageBorderRadius})`
                            }}
                        />
                    </div>
                </main>
            </div>
        </>
    );
}
